import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, ArrowLeft, Home, Zap, GraduationCap, Clock, AlertCircle, Baby, Dog, ChevronRight } from 'lucide-react';
import { SEO } from '@/components/SEO';

const QUESTIONS = [
  {
    id: 'living_situation',
    title: 'Where do you live?',
    subtitle: 'Your living space affects the ideal dog size and energy level',
    icon: Home,
    options: [
      { value: 'apartment', label: 'Apartment', desc: 'Small space, no yard' },
      { value: 'house_small', label: 'House (small yard)', desc: 'Moderate outdoor space' },
      { value: 'house_large', label: 'House (large yard)', desc: 'Plenty of room to roam' },
    ]
  },
  {
    id: 'activity_level',
    title: 'How active are you?',
    subtitle: 'Match your energy with your future companion',
    icon: Zap,
    options: [
      { value: 'low', label: 'Low', desc: 'Prefer relaxing at home' },
      { value: 'moderate', label: 'Moderate', desc: 'Regular walks, some outdoor activities' },
      { value: 'high', label: 'High', desc: 'Running, hiking, very active lifestyle' },
    ]
  },
  {
    id: 'experience',
    title: 'Experience with dogs?',
    subtitle: 'Some breeds are better suited for experienced owners',
    icon: GraduationCap,
    options: [
      { value: 'first_time', label: 'First-time owner', desc: 'Never owned a dog before' },
      { value: 'some', label: 'Some experience', desc: 'Had dogs growing up or briefly' },
      { value: 'experienced', label: 'Experienced', desc: 'Currently or recently owned dogs' },
    ]
  },
  {
    id: 'time_availability',
    title: 'How much time can you dedicate?',
    subtitle: 'Dogs need attention, grooming, and exercise',
    icon: Clock,
    options: [
      { value: 'limited', label: 'Limited', desc: '1-2 hours per day' },
      { value: 'moderate', label: 'Moderate', desc: '2-4 hours per day' },
      { value: 'lots', label: 'Plenty', desc: '4+ hours per day' },
    ]
  },
  {
    id: 'special',
    title: 'Any special considerations?',
    subtitle: 'Select all that apply',
    icon: AlertCircle,
    type: 'multi',
    options: [
      { id: 'allergies', label: 'Allergies', desc: 'Need a hypoallergenic breed', icon: AlertCircle },
      { id: 'kids', label: 'Have kids', desc: 'Need a kid-friendly breed', icon: Baby },
    ]
  },
];

export default function DogMatcherPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [specialChecks, setSpecialChecks] = useState({ allergies: false, kids: false });

  const question = QUESTIONS[step];
  const progress = ((step + 1) / QUESTIONS.length) * 100;
  const isLastStep = step === QUESTIONS.length - 1;

  const handleSelect = (value) => {
    if (question.type === 'multi') return;
    setAnswers(prev => ({ ...prev, [question.id]: value }));
    if (!isLastStep) {
      setTimeout(() => setStep(s => s + 1), 300);
    }
  };

  const toggleSpecial = (id) => {
    setSpecialChecks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        living_situation: answers.living_situation || 'apartment',
        activity_level: answers.activity_level || 'moderate',
        experience: answers.experience || 'first_time',
        time_availability: answers.time_availability || 'moderate',
        allergies: specialChecks.allergies,
        kids: specialChecks.kids,
      };
      const res = await api.submitMatcher(payload);
      setResults(res.data.matches || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Results view
  if (results !== null) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="matcher-results">
        <h1 className="font-['Nunito'] font-extrabold text-3xl sm:text-4xl text-[#1A1D20] tracking-tight text-center mb-2">
          Your Perfect Matches
        </h1>
        <p className="text-center text-[#5C636A] mb-8">Based on your lifestyle, here are the best breeds for you</p>

        {results.length > 0 ? (
          <div className="space-y-4">
            {results.map((match, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-shadow"
                data-testid={`match-result-${i}`}
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-40 h-40 shrink-0">
                    <img src={match.breed.image_url} alt={match.breed.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-['Nunito'] font-extrabold text-xl text-[#1A1D20]">
                            #{i + 1}
                          </span>
                          <h3 className="font-['Nunito'] font-bold text-lg text-[#1A1D20]">{match.breed.name}</h3>
                        </div>
                        <p className="text-sm text-[#5C636A]">{match.breed.size} &middot; {match.breed.energy_level} Energy</p>
                      </div>
                      <Badge className="bg-[#7ED321] text-white border-0 rounded-full font-bold px-3">
                        {Math.min(Math.round((match.score / 15) * 100), 99)}% Match
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {match.reasons.map((r, j) => (
                        <Badge key={j} className="bg-blue-50 text-[#4A90E2] border-0 rounded-full text-xs px-2.5 py-0.5">
                          {r}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/dog-breeds/${match.breed.slug}`)}
                        className="rounded-full text-xs"
                        data-testid={`match-learn-more-${i}`}
                      >
                        Learn More
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/dogs-for-sale?breed=${encodeURIComponent(match.breed.name)}`)}
                        className="rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white text-xs cta-button"
                        data-testid={`match-find-${i}`}
                      >
                        Find {match.breed.name} <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-[#5C636A]">No matches found. Try adjusting your answers.</p>
          </div>
        )}

        <div className="text-center mt-8">
          <Button
            variant="outline"
            onClick={() => { setResults(null); setStep(0); setAnswers({}); setSpecialChecks({ allergies: false, kids: false }); }}
            className="rounded-full"
            data-testid="retake-quiz-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retake Quiz
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="dog-matcher-page">
      <SEO
        title="Dog Breed Matcher"
        description="What dog should I get? Take our dog breed matcher quiz to find the perfect dog for your lifestyle."
        keywords="what dog should I get, dog breed selector, best dog for my lifestyle, dog compatibility quiz"
      />
      <div className="text-center mb-8">
        <h1 className="font-['Nunito'] font-extrabold text-3xl sm:text-4xl text-[#1A1D20] tracking-tight mb-2">
          Dog Breed Matcher
        </h1>
        <p className="text-[#5C636A]">Answer a few questions and we'll find your ideal breed</p>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between text-xs text-[#5C636A] mb-2">
          <span>Question {step + 1} of {QUESTIONS.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2 bg-blue-100 [&>div]:bg-[#4A90E2]" data-testid="matcher-progress" />
      </div>

      {/* Question */}
      <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`question-${question.id}`}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center">
            <question.icon className="w-6 h-6 text-[#4A90E2]" />
          </div>
          <div>
            <h2 className="font-['Nunito'] font-bold text-xl text-[#1A1D20]">{question.title}</h2>
            <p className="text-sm text-[#5C636A]">{question.subtitle}</p>
          </div>
        </div>

        <div className="space-y-3">
          {question.type === 'multi' ? (
            <>
              {question.options.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => toggleSpecial(opt.id)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-colors ${
                    specialChecks[opt.id] ? 'border-[#4A90E2] bg-blue-50' : 'border-gray-100 hover:border-gray-200'
                  }`}
                  data-testid={`option-${opt.id}`}
                >
                  <p className="font-semibold text-[#1A1D20]">{opt.label}</p>
                  <p className="text-sm text-[#5C636A]">{opt.desc}</p>
                </button>
              ))}
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full mt-4 rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-bold h-12 shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
                data-testid="submit-quiz-button"
              >
                {loading ? 'Finding Your Match...' : 'Find My Match'} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            question.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-colors ${
                  answers[question.id] === opt.value ? 'border-[#4A90E2] bg-blue-50' : 'border-gray-100 hover:border-gray-200'
                }`}
                data-testid={`option-${opt.value}`}
              >
                <p className="font-semibold text-[#1A1D20]">{opt.label}</p>
                <p className="text-sm text-[#5C636A]">{opt.desc}</p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Navigation */}
      {step > 0 && (
        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            onClick={() => setStep(s => s - 1)}
            className="text-sm text-[#5C636A]"
            data-testid="prev-question-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
        </div>
      )}
    </div>
  );
}
